import { useState } from "react";
import { toast } from "sonner";
import {
    Sparkles, CheckCircle2, Clock, XCircle, Repeat, Loader2, ArrowRight, Info, X, CalendarClock,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * V169 — Demo Outcome Panel + Convert-to-Regular flow.
 *
 * Renders inside AssignmentDetailModal → Overview tab, only when
 * assignment.is_demo === true. Two responsibilities:
 *   1. Capture the post-demo decision (interested / follow_up / not_interested / another_demo).
 *   2. When "interested" → open a mobile-first dialog to generate a recurring plan.
 */

const OUTCOMES = [
    { id: "interested",     label: "Parent Interested",     tone: "bg-emerald-600 hover:bg-emerald-700 text-white", pill: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
    { id: "follow_up",      label: "Follow-up Required",    tone: "bg-amber-600 hover:bg-amber-700 text-white",   pill: "bg-amber-50 text-amber-800 border-amber-200",     icon: Clock },
    { id: "another_demo",   label: "Schedule Another Demo", tone: "bg-cyan-600 hover:bg-cyan-700 text-white",     pill: "bg-cyan-50 text-cyan-800 border-cyan-200",         icon: Repeat },
    { id: "not_interested", label: "Not Interested",        tone: "bg-slate-600 hover:bg-slate-700 text-white",   pill: "bg-slate-100 text-slate-700 border-slate-200",     icon: XCircle },
];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function DemoOutcomePanel({ assignment, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [showConvert, setShowConvert] = useState(false);
    const outcome = assignment?.demo_outcome;

    const setOutcome = async (id) => {
        setBusy(true);
        try {
            await api.post(`/admin/assignments/${assignment.id}/demo/outcome`, { outcome: id });
            toast.success("Outcome saved");
            onChanged?.();
            if (id === "interested") setShowConvert(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save outcome");
        } finally { setBusy(false); }
    };

    const outcomeMeta = OUTCOMES.find((o) => o.id === outcome);

    return (
        <section
            className="bg-gradient-to-br from-fuchsia-50 to-cyan-50 border-2 border-fuchsia-200 rounded-2xl p-4"
            data-testid="demo-outcome-panel"
        >
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Demo Class
            </div>
            <div className="text-lg font-heading font-bold text-[#0F172A] mt-0.5">
                Demo Completed — What next?
            </div>
            <p className="text-xs text-slate-700 mt-1">
                Pick an outcome for this demo. If the parent is interested, you can generate the recurring plan in one tap — no retyping.
            </p>

            {outcomeMeta && (
                <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${outcomeMeta.pill}`} data-testid="demo-outcome-pill">
                    <outcomeMeta.icon className="w-3.5 h-3.5" />
                    Current: {outcomeMeta.label}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-testid="demo-outcome-buttons">
                {OUTCOMES.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        onClick={() => setOutcome(o.id)}
                        disabled={busy}
                        data-testid={`demo-outcome-${o.id}`}
                        data-cm-primary
                        aria-pressed={outcome === o.id ? "true" : "false"}
                        className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-60 ${
                            outcome === o.id
                                ? `${o.tone} ring-2 ring-offset-2 ring-offset-fuchsia-50 ring-cyan-400`
                                : `${o.tone}`
                        }`}
                    >
                        <o.icon className="w-4 h-4" />
                        {o.label}
                    </button>
                ))}
            </div>

            {outcome === "interested" && (
                <button
                    type="button"
                    onClick={() => setShowConvert(true)}
                    data-testid="demo-convert-btn"
                    data-cm-primary
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-[#0F172A] text-white text-sm font-bold uppercase tracking-wider px-4 py-2 rounded-lg hover:bg-[#1E293B]"
                >
                    Convert to Regular Classes <ArrowRight className="w-4 h-4" />
                </button>
            )}

            {showConvert && (
                <ConvertDemoDialog
                    assignment={assignment}
                    onClose={() => setShowConvert(false)}
                    onConverted={() => { setShowConvert(false); onChanged?.(); }}
                />
            )}
        </section>
    );
}

// ---------------------------------------------------------- Convert dialog

function ConvertDemoDialog({ assignment, onClose, onConverted }) {
    const [f, setF] = useState({
        total_sessions: 12,
        sessions_per_week: 2,
        session_duration_minutes: 60,
        days_of_week: [1, 3],           // Tue, Thu
        start_time: "18:00",
        start_date: nextMondayIso(),
        meeting_url_default: "",
        package_fee: "",
        tutor_rate: "",
    });
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);

    const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

    const toggleDay = (d) => {
        setF((prev) => ({
            ...prev,
            days_of_week: prev.days_of_week.includes(d)
                ? prev.days_of_week.filter((x) => x !== d)
                : [...prev.days_of_week, d].sort((a, b) => a - b),
        }));
    };

    const canSubmit = f.total_sessions > 0
        && f.sessions_per_week > 0
        && f.days_of_week.length === f.sessions_per_week
        && f.start_time
        && f.start_date
        && !saving;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const url = (f.meeting_url_default || "").trim();
        if (url && !/^https?:\/\//i.test(url)) {
            toast.error("Meeting URL must start with http:// or https://");
            return;
        }
        setSaving(true);
        try {
            const r = await api.post(`/admin/assignments/${assignment.id}/demo/convert`, {
                total_sessions: Number(f.total_sessions),
                sessions_per_week: Number(f.sessions_per_week),
                session_duration_minutes: Number(f.session_duration_minutes),
                days_of_week: f.days_of_week,
                start_time: f.start_time,
                start_date: f.start_date,
                meeting_url_default: url || null,
                package_fee: f.package_fee === "" ? null : Number(f.package_fee),
                tutor_rate: f.tutor_rate === "" ? null : Number(f.tutor_rate),
            });
            toast.success(`Recurring plan created · ${r.data.sessions_generated} sessions`);
            setResult(r.data);
            onConverted?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not convert"));
        } finally { setSaving(false); }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[95] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            data-testid="demo-convert-dialog"
        >
            <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between z-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Convert to Regular</div>
                        <div className="text-lg font-heading font-bold text-[#0F172A] truncate">{assignment?.student_name}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="demo-convert-close"
                        data-cm-compact
                        aria-label="Close"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {result ? (
                    <div className="p-4 sm:p-5 space-y-3" data-testid="demo-convert-success">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Converted</div>
                            <div className="text-lg font-heading font-bold text-[#0F172A] mt-0.5">
                                {result.sessions_generated} sessions scheduled
                            </div>
                            <div className="text-xs text-slate-700 mt-1 inline-flex items-center gap-1">
                                <CalendarClock className="w-3.5 h-3.5" /> {formatDay(result.first_session_at)} → {formatDay(result.last_session_at)}
                            </div>
                        </div>
                        <p className="text-xs text-slate-600">
                            Parents & tutors see the full recurring schedule instantly. Edit any single session (URL, time, reschedule) from the Sessions tab.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="demo-convert-done"
                            data-cm-primary
                            className="w-full bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1E293B]"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit}>
                        <div className="p-4 sm:p-5 space-y-4">
                            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 text-xs text-cyan-900 flex items-start gap-2">
                                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                                <div>
                                    Student, parent, tutor and subject stay the same — we're just adding the recurring schedule. The demo session already on record is preserved.
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <FormField label="Total sessions *">
                                    <input type="number" min={1} max={200} value={f.total_sessions} onChange={(e) => set("total_sessions", Number(e.target.value))} data-testid="convert-total-sessions" className={inp} />
                                </FormField>
                                <FormField label="Per week *">
                                    <input type="number" min={1} max={7} value={f.sessions_per_week} onChange={(e) => set("sessions_per_week", Number(e.target.value))} data-testid="convert-sessions-per-week" className={inp} />
                                </FormField>
                            </div>

                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">
                                    Class days * <span className="text-slate-400 font-normal">— pick {f.sessions_per_week}</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5" data-testid="convert-days-picker">
                                    {WEEKDAYS.map((label, i) => {
                                        const active = f.days_of_week.includes(i);
                                        return (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => toggleDay(i)}
                                                data-testid={`convert-day-${label.toLowerCase()}`}
                                                data-active={active ? "true" : "false"}
                                                data-cm-compact
                                                className={`inline-flex items-center justify-center min-w-[42px] min-h-[36px] text-xs font-semibold rounded-full border ${
                                                    active
                                                        ? "bg-[#0F172A] text-white border-[#0F172A]"
                                                        : "bg-white text-slate-700 border-slate-200 hover:border-cyan-300"
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {f.days_of_week.length !== f.sessions_per_week && (
                                    <div className="text-[11px] text-rose-700 mt-1">
                                        Pick exactly {f.sessions_per_week} day(s) — you have {f.days_of_week.length}.
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <FormField label="Start date *">
                                    <input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="convert-start-date" className={inp} />
                                </FormField>
                                <FormField label="Start time *">
                                    <input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="convert-start-time" className={inp} />
                                </FormField>
                            </div>

                            <FormField label="Session duration (mins)">
                                <input type="number" min={15} max={600} step={15} value={f.session_duration_minutes} onChange={(e) => set("session_duration_minutes", Number(e.target.value))} data-testid="convert-duration" className={inp} />
                            </FormField>

                            <FormField label="Default meeting URL (applied to every new session)">
                                <input type="url" value={f.meeting_url_default} onChange={(e) => set("meeting_url_default", e.target.value)} data-testid="convert-meeting-url" className={inp} placeholder="https://meet.google.com/abc-defg-hij" />
                                <div className="text-[10px] text-slate-500 mt-1">Optional. You can override per-session from the Sessions tab.</div>
                            </FormField>

                            <div className="grid grid-cols-2 gap-3">
                                <FormField label="Package fee (₹)">
                                    <input type="number" min={0} step={100} value={f.package_fee} onChange={(e) => set("package_fee", e.target.value)} data-testid="convert-package-fee" className={inp} placeholder="Optional" />
                                </FormField>
                                <FormField label="Tutor rate (₹/session)">
                                    <input type="number" min={0} step={100} value={f.tutor_rate} onChange={(e) => set("tutor_rate", e.target.value)} data-testid="convert-tutor-rate" className={inp} placeholder="Optional" />
                                </FormField>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 sm:px-5 py-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={saving}
                                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="demo-convert-submit"
                                data-cm-primary
                                className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                                Generate {f.total_sessions} sessions
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------- helpers

function FormField({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
            {children}
        </label>
    );
}
const inp = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none bg-white";

function nextMondayIso() {
    const d = new Date();
    // Advance to the next Monday. If today is Monday, target the Monday
    // after (7 days) so admins don't accidentally schedule an in-progress day.
    const daysToMon = ((1 - d.getDay()) + 7) % 7 || 7;
    d.setDate(d.getDate() + daysToMon);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDay(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso || "—"; }
}
